import React, { useState, useEffect } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { CalendarEvent } from "../Common/types";
import {
  Box,
  Link,
  Tooltip,
  Text,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast,
  Button,
  VStack,
  Heading,
  Flex,
  Input,
  Select,
  HStack,
  useBreakpointValue,
} from "@chakra-ui/react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { StudentInfo } from "../Common/types";
import { useNavigate } from "react-router-dom";
import { useTenant } from "../Routers/TenantContextProvider";

const localizer = momentLocalizer(moment);

interface TokenPayload {
  sub: string;
  role_id: number;
  tenant_id: number;
  exp?: number;
}

const API_URL = "https://your-backend-api.com/student/events";

const initialStudentInfo: StudentInfo = {
      givenName: '',
      middlename: '',
      surname: '',
      email: '',
      contactNo: '',
      gender: '',
      address: '',
      suburb: '',
      postcode: '',
      schoolName: '',
      schoolGrade: '',
      dateOfBirth: null,
      student_id: 0,
      is_active: true,
      user_id: 0,
      actioned_by: '',
      created_at: new Date(),
      updated_at: new Date(),
      enrolmentMode: 'offline',   // NEW — required field on the type, was missing here.
                                  // Without this the file fails to compile (TS2741: property
                                  // 'enrolmentMode' is missing) the same way ReviewEnrolment did.
  };

const StudentProfile = () => {

  const [currentStudent, setCurrentStudent] = useState<StudentInfo>(initialStudentInfo);

  const toast = useToast();

  const { tenant, loading, error } = useTenant();

  const tableHeaderBg = useColorModeValue("teal.100", "teal.700");
  const calendarBg = useColorModeValue("blue.50", "blue.700");
  const buttonBg = useColorModeValue("red.500", "red.700");
  const textColor = useColorModeValue("blue.800", "blue.50");

  const bannerColor = useColorModeValue("yellow.100", "yellow.700");

  const showBanner = useBreakpointValue({ base: false, md: true });

  const navigate = useNavigate();

  const [isEditing, setIsEditing] = useState(false);

  const isOnline = currentStudent.enrolmentMode === 'online';   // NEW

  const handleEdit = () => setIsEditing(true);

  const fetchCurrentStudent = async () => {
    try {
      const apiurl = process.env.REACT_APP_API_URL || "https://default-url.com";

      const response = await axios.get(apiurl + `/students-info`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setCurrentStudent(response.data);
    } catch (error) {
      console.error("Error fetching Student Info:", error);
    }
  };

  const handleChangePassword = () => {
     if (tenant)
    {
      navigate(`/platform/${tenant.tenant_slug}/app/change-password`);
    }
  }

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleSave = async () => {
    setIsEditing(false);

    console.log("Saving student info:", currentStudent);

    try {
        const apiurl = process.env.REACT_APP_API_URL || "https://default-url.com";

          const response = await axios.put(apiurl + `/update-student-info/${currentStudent.student_id}`, currentStudent, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          });
          setCurrentStudent(response.data);
          toast({ title: "Details Successfully updated" });
        } catch (error) {
          console.error("Error updating Student Info:", error);
          toast({ title: "Error updating details", status: "error" });
          fetchCurrentStudent();
        }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setCurrentStudent((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = event.target;
    setCurrentStudent((prev) => ({
      ...prev,
      [name]: value ? new Date(value) : null,
    }));
  };

  useEffect(() => {
    fetchCurrentStudent();
    console.log("Current Date of Birth:", currentStudent.dateOfBirth);
  }, []);

  return (

    <Box>
    {showBanner && (
    <Box bgColor={bannerColor} p={4} height={"70px"}>
      <HStack>
        <Heading size={"sm"}>Hello  {currentStudent?.givenName} ! </Heading>
        <Text fontWeight={"normal"} fontSize={"md"}>Checkout your profile information, feel free to update in case something is incorrect!!</Text>
        </HStack>
    </Box>
    )}
    {!showBanner && (
    <Box p={4} bgColor={bannerColor} height={"50px"}>
    <VStack>
        <Text fontWeight={"semibold"} fontSize={"md"}>My Profile</Text>
        </VStack>
    </Box>
    )}

    <Box as="main" mx="auto" color={textColor} maxWidth={"1200px"}>

        <Box ml={2} mr={2} p={6} >
        <Flex justifyContent="space-between" alignItems="center">

            {isEditing ? (
            <HStack spacing={2}>
                <Button colorScheme="blue" onClick={handleSave} size="sm">
                Save
                </Button>
                <Button colorScheme="red" mr={4} onClick={handleCancel} size="sm">
                Cancel
                </Button>
            </HStack>
            ) : (
                <HStack spacing={2}>
            <Button colorScheme="blue" mr={4} onClick={handleEdit} size="sm">
                Edit Profile
            </Button>
             <Button colorScheme="pink" mr={4} onClick={handleChangePassword} size="sm">
             Change Password
            </Button>
            </HStack>
            )}
        </Flex>
        </Box>

            {/* Identity Details */}
            <Box
            ml={8}
            mr={8}
            bgColor={calendarBg}
            borderRadius="md"
            p={4}
            boxShadow="lg"
            >
            <Text mb={2} fontSize="md" fontWeight="bold">
                Identity Details
            </Text>

            <Text
                mb={4}
                fontSize="sm"
                color="gray.500"
            >
                Identity details, including your name, date of birth, gender and email
                address, can only be changed by Eduvic. Please log a support request if
                any of these details need to be corrected.
            </Text>

            <Table variant="simple" size="sm">
                <Tbody>

                {/* Given Name */}
                <Tr>
                    <Td fontWeight="bold" width="365px" height="48px">
                    Given Name
                    </Td>
                    <Td>
                    {currentStudent.givenName || "N/A"}
                    </Td>
                </Tr>

                {/* Middle Name */}
                <Tr>
                    <Td fontWeight="bold" width="365px" height="48px">
                    Middle Name
                    </Td>
                    <Td>
                    {currentStudent.middlename || "N/A"}
                    </Td>
                </Tr>

                {/* Surname */}
                <Tr>
                    <Td fontWeight="bold" width="365px" height="48px">
                    Surname
                    </Td>
                    <Td>
                    {currentStudent.surname || "N/A"}
                    </Td>
                </Tr>

                {/* Gender */}
                <Tr>
                    <Td fontWeight="bold" width="365px" height="48px">
                    Gender
                    </Td>
                    <Td>
                    {currentStudent.gender || "N/A"}
                    </Td>
                </Tr>

                {/* Date of Birth */}
                <Tr>
                    <Td fontWeight="bold" width="365px" height="48px">
                    Date of Birth
                    </Td>
                    <Td>
                    {currentStudent.dateOfBirth
                        ? new Date(currentStudent.dateOfBirth).toLocaleDateString()
                        : "N/A"}
                    </Td>
                </Tr>

                {/* Email */}
                <Tr>
                    <Td fontWeight="bold" width="365px" height="48px">
                    Email
                    </Td>
                    <Td>
                    {currentStudent.email || "N/A"}
                    </Td>
                </Tr>

                </Tbody>
            </Table>
            </Box>

            {/* Address & Contact Details Section */}
            <Box mt={6} ml={8} mr={8} bgColor={calendarBg} borderRadius="md" p={4} boxShadow="lg">
            <Text mb={4} fontSize="md" fontWeight="bold">
                Address & Contact Details
            </Text>
            <Table variant="simple" size="sm">
                <Tbody>
              
                  <>
                    <Tr>
                        <Td fontWeight="bold" width="365px" height="48px">Address</Td>
                        <Td>
                        {isEditing ? (
                            <Input
                            name="address"
                            placeholder="Address*"
                            size="sm"
                            value={currentStudent.address ?? ''}
                            onChange={handleChange}
                            />
                        ) : (
                            currentStudent.address || "N/A"
                        )}
                        </Td>
                    </Tr>
                    <Tr>
                        <Td fontWeight="bold" width="365px" height="48px">Suburb</Td>
                        <Td>
                        {isEditing ? (
                            <Input
                            name="suburb"
                            placeholder="Suburb*"
                            size="sm"
                            value={currentStudent.suburb ?? ''}
                            onChange={handleChange}
                            />
                        ) : (
                            currentStudent.suburb || "N/A"
                        )}
                        </Td>
                    </Tr>
                    <Tr>
                        <Td fontWeight="bold" width="365px" height="48px">Postcode</Td>
                        <Td>
                        {isEditing ? (
                            <Input
                            name="postcode"
                            placeholder="Postcode*"
                            size="sm"
                            value={currentStudent.postcode ?? ''}
                            onChange={handleChange}
                            />
                        ) : (
                            currentStudent.postcode || "N/A"
                        )}
                        </Td>
                    </Tr>
                  </>
               
{/*                 <Tr>
                    <Td fontWeight="bold" width="365px" height="48px">Email</Td>
                    <Td>
                    {isEditing ? (
                        <Input
                        name="email"
                        type="email"
                        placeholder="Student's Email*"
                        size="sm"
                        value={currentStudent.email}
                        onChange={handleChange}
                        />
                    ) : (
                        currentStudent.email || "N/A"
                    )}
                    </Td>
                </Tr> */}
                <Tr>
                    <Td fontWeight="bold" width="365px" height="48px">Contact No.</Td>
                    <Td>
                    {isEditing ? (
                        <Input
                        name="contactNo"
                        placeholder="Contact No."
                        size="sm"
                        value={currentStudent.contactNo ?? ''}
                        onChange={handleChange}
                        />
                    ) : (
                        currentStudent.contactNo || "N/A"
                    )}
                    </Td>
                </Tr>
                </Tbody>
            </Table>
            </Box>

            {/* Education Details Section */}
            <Box mt={6} ml={8} mr={8} bgColor={calendarBg} borderRadius="md" p={4} boxShadow="lg" mb={6}>
            <Text mb={4} fontSize="md" fontWeight="bold">
                Education Details
            </Text>
            <Table variant="simple" size="sm">
                <Tbody>
                <Tr>
                    <Td fontWeight="bold" width="365px" height="48px">School Name</Td>
                    <Td>
                    {isEditing ? (
                        <Input
                        name="schoolName"
                        placeholder="Student's School Name*"
                        size="sm"
                        value={currentStudent.schoolName}
                        onChange={handleChange}
                        />
                    ) : (
                        currentStudent.schoolName || "N/A"
                    )}
                    </Td>
                </Tr>
                <Tr>
                    <Td fontWeight="bold" width="365px" height="48px">School Grade</Td>
                    <Td>
                    {isEditing ? (
                        <Input
                        name="schoolGrade"
                        placeholder="Student's School Grade*"
                        size="sm"
                        value={currentStudent.schoolGrade}
                        onChange={handleChange}
                        />
                    ) : (
                        currentStudent.schoolGrade || "N/A"
                    )}
                    </Td>
                </Tr>
                </Tbody>
            </Table>
            </Box>

  </Box>
  </Box>
  );
};

export default StudentProfile;